import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { prop } from 'ramda';
import { log } from 'log';
import { useTranslations } from '@core/js/hooks/i18n';
import { usePrevious } from '@core/js/hooks/usePrevious';
import { useIsMounted } from '@core/js/hooks/useIsMounted';
import { handleVerifyClick, handlePostMessage } from '../helpers';
import { useUserSelector, useI18nSelector, useAppSelector } from '@core/js/store/hooks';
import { useGetPlayerTags } from 'server';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { POPUP_IDS, useOnClose } from '@core/js/components/common/PopupManager';

import { TAG_NAME } from '@core/js/components/popups/JumioVerifyPopup/constants';
import * as actions from '../store/actions';

export const useJumioVerifyPopup = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isMounted = useIsMounted();
    const [tagValue, setTagValue] = useState('');
    const [isJumioShown, setIsJumioShown] = useState(false);
    const [isLoadingPlayerTag, setIsLoadingPlayerTag] = useState(false);
    const translations = useI18nSelector(prop('translations'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const prevIsLoggedIn = usePrevious(isLoggedIn);
    const translate = useTranslations('playtech.mobile.system.jumio.');
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const getPlayerTags = useGetPlayerTags();
    const hasTranslationsLoaded = Object.keys(translations).length > 0;

    const showJumioVerifyPopup = useCallback(popupData => dispatch(actions.showJumioVerifyPopupAction(popupData)), [dispatch]);

    /* Lugano changes */
    const closeProvisionalPopup = useOnClose(POPUP_IDS.WELCOME_POPUP);
    const onClosePopup = useOnClose();
    const showNotification = useShowNotification();
    /* Lugano changes */

    useEffect(() => {
        if (dynamicTags.DTAG_DISABLE_JUMIO_VERIFY_POPUP) {
            return;
        }

        (async () => {
            try {
                if (!isLoggedIn) {
                    return;
                }

                setIsLoadingPlayerTag(true);

                const { tags } = await getPlayerTags({ tagName: TAG_NAME });
                const value = tags?.find((tag) => tag.name === TAG_NAME)?.value;

                if (isMounted()) {
                    value && setTagValue(value);
                    setIsLoadingPlayerTag(false);
                }
            } catch (error) {
                log('error', error);
            }
        })();
    }, [dynamicTags, getPlayerTags, isMounted, isLoggedIn, setIsLoadingPlayerTag, setTagValue]);

    /* Lugano changes */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onClosePopupError = () => showNotification('playtech.mobile.system.jumio.error', { variant: 'error' });

    const showJumioIframePopup = useCallback(
        () => {
            closeProvisionalPopup();
            return handleVerifyClick(dispatch, onClosePopupError);
        },
        [closeProvisionalPopup, dispatch, onClosePopupError]
    );
    /* Lugano changes */

    useEffect(() => {
        if (dynamicTags.DTAG_DISABLE_JUMIO_VERIFY_POPUP) {
            return;
        }

        if (!hasTranslationsLoaded || isLoadingPlayerTag) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const isHideKYC = params.get('hideKYC') === '1';

        if (isHideKYC || !isLoggedIn || tagValue !== 'mandatory' || isJumioShown) {
            return;
        }

        const content = translate('content');

        setIsJumioShown(true);
        showJumioVerifyPopup({
            content,
            onAccept: showJumioIframePopup /* Lugano changes */
        });
    }, [
        location,
        tagValue,
        translate,
        isLoggedIn,
        dynamicTags,
        isJumioShown,
        hasTranslationsLoaded,
        showJumioVerifyPopup,
        isLoadingPlayerTag,
        showJumioIframePopup
    ]);

    /* Lugano changes */
    useEffect(() => {
        const handleMessage = handlePostMessage(onClosePopup, showNotification);
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [onClosePopup, showNotification]);
    /* Lugano changes */

    useEffect(() => {
        if (!prevIsLoggedIn || isLoggedIn) {
            return;
        }

        setIsJumioShown(false);
        setTagValue('');
    }, [prevIsLoggedIn, isLoggedIn, setTagValue, setIsJumioShown]);
};
